import AsyncStorage from '@react-native-async-storage/async-storage'
import { Alert } from 'react-native'

export type StorageKey =
  | 'access_token'
  | 'adjust_beneficiary_event_sent'
  | 'campaign_date'
  | 'cookies'
  | 'credit_review_profile_started_at'
  | 'device_id'
  | 'first_time_review_has_been_requested'
  | 'has_seen_birthday_notification_card'
  | 'has_seen_booking_page'
  | 'has_seen_share_app_modal'
  | 'has_seen_eligible_card'
  | 'has_seen_follow_venue_fake_door_survey'
  | 'has_seen_follow_artist_fake_door_survey'
  | 'has_seen_onboarding_subscription'
  | 'has_seen_push_notifications_modal_once'
  | 'has_seen_qualtrics_survey'
  | 'has_seen_tutorials'
  | 'logged_in_session_count'
  | 'offers_viewed_count'
  | 'PASSCULTURE_REFRESH_TOKEN'
  | 'profile-address'
  | 'profile-city'
  | 'profile-name'
  | 'profile-status'
  | 'phone_validation_code_asked_at'
  | 'react_navigation_persistence'
  | 'review_request_history'
  | 'review_v2_migration_done'
  | 'times_location_tooltip_has_been_displayed'
  | 'times_subscription_tooltip_has_been_displayed'
  | 'times_review_has_been_requested'
  | 'traffic_campaign'
  | 'traffic_content'
  | 'traffic_gen'
  | 'traffic_medium'
  | 'traffic_source'
  | 'user_age'
  | 'times_user_subscribed_to_a_theme'
  | 'times_music_live_booking_survey_has_been_displayed'
  | '@passculture.venue.video_seen'

export const storage = {
  clear,
  getAllKeys,
  readObject,
  readString,
  readMultiString,
  saveObject,
  saveString,
  saveMultiString,
}

async function readString(storageKey: StorageKey): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(storageKey)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
    return null
  }
}

async function readMultiString(
  storageKeys: StorageKey[]
): Promise<readonly [string, string | null][]> {
  try {
    return await AsyncStorage.multiGet(storageKeys)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
    return []
  }
}

async function saveString(storageKey: StorageKey, value: string): Promise<void> {
  if (!value) {
    throw new Error('Aucune valeur à sauvegarder')
  }
  try {
    await AsyncStorage.setItem(storageKey, value)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
  }
}

async function saveMultiString(keyValuePairs: Array<[StorageKey, string]>): Promise<void> {
  if (!keyValuePairs.length) {
    throw new Error('Aucune valeur à sauvegarder')
  }
  try {
    await AsyncStorage.multiSet(keyValuePairs)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
  }
}

async function readObject<ObjectType>(storageKey: StorageKey): Promise<ObjectType | null> {
  try {
    const stringifiedObject = await AsyncStorage.getItem(storageKey)
    if (stringifiedObject) {
      return JSON.parse(stringifiedObject)
    }
    return null
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
    return null
  }
}

async function saveObject(storageKey: StorageKey, value: unknown): Promise<void> {
  try {
    const stringifiedValue = JSON.stringify(value)
    await AsyncStorage.setItem(storageKey, stringifiedValue)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
  }
}

async function clear(storageKey: StorageKey): Promise<void> {
  try {
    await AsyncStorage.removeItem(storageKey)
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
  }
}

async function getAllKeys(): Promise<readonly StorageKey[] | void> {
  try {
    return (await AsyncStorage.getAllKeys()) as StorageKey[]
  } catch (error) {
    if (error instanceof Error) onAsyncStorageError(error)
  }
}

function onAsyncStorageError(error: Error | undefined): void {
  const stringifiedError = JSON.stringify(error)
  Alert.alert('AsyncStorage Error', stringifiedError)
}
